import { Control, FieldValues, useFormState } from 'react-hook-form';

/**
 * Walks a dot-separated path (e.g. `settings.tempSetpoint`) into a nested
 * `dirtyFields` object. The form-level `dirtyFields` map is shaped like the
 * form state itself, so a leaf-field path resolves to `true` (dirty) or
 * `undefined` (clean).
 */
const traverseDirtyFields = (dirtyFields: Record<string, unknown>, path: string): boolean =>
  path
    .split('.')
    .reduce<unknown>((obj, key) => (obj as Record<string, unknown>)?.[key], dirtyFields) === true;

/**
 * Dirty-state hook for a single setting field. Scopes the RHF subscription to
 * the named field's slot via `useFormState({ name })` so toggling one field
 * does not re-render its siblings.
 *
 * Reads form-level `dirtyFields` (not `fieldState.isDirty`) because the
 * equipment-details form is configured with `keepDirtyValues: true` and syncs
 * external state via the `values` prop. RHF's `fieldState.isDirty` does not
 * account for that sync and can report stale results; `dirtyFields` does.
 *
 * Pass the full dotted field path (e.g. `settings.<id>` or
 * `sequencerSettings.<id>`) so the subscription is correctly scoped.
 */
export const useFieldIsDirty = <T extends FieldValues>(
  control: Control<T>,
  fieldPath: string
): boolean => {
  const { dirtyFields } = useFormState({ control, name: fieldPath as never });
  if (!fieldPath) return false;
  return traverseDirtyFields(dirtyFields as Record<string, unknown>, fieldPath);
};
